import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { changePasswordRequestSchema } from '@ace-tracker/shared';
import { changePassword, UnauthorizedError } from '../../api';
import { useAuth } from '../../auth';
import { useTheme, type Theme } from '../../theme';
import { Button } from '../Button';
import { Card, Field, TextInput } from './formParts';

const THEMES: { value: Theme; label: string; icon: string }[] = [
  { value: 'light', label: 'Light', icon: 'fa-solid fa-sun' },
  { value: 'dark', label: 'Dark', icon: 'fa-solid fa-moon' },
];

/** Settings → Account: per-browser appearance, and the dashboard login's password. */
export function AccountSettings() {
  return (
    <div className="space-y-5">
      <AppearanceCard />
      <ChangePasswordCard />
    </div>
  );
}

function AppearanceCard() {
  const { theme, setTheme } = useTheme();
  return (
    <Card title="Appearance" description="Saved in this browser only.">
      <fieldset>
        <legend className="text-sm font-medium text-text-default">Theme</legend>
        <div className="mt-2 flex flex-wrap gap-3">
          {THEMES.map((t) => {
            const selected = theme === t.value;
            return (
              <label
                key={t.value}
                className={`flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-input border px-4 text-sm focus-within:outline focus-within:outline-2 focus-within:outline-sky-blue ${
                  selected ? 'border-primary-blue bg-surface-alt font-bold text-primary-blue' : 'border-border text-text-muted hover:text-text-default'
                }`}
              >
                <input
                  type="radio"
                  name="theme"
                  value={t.value}
                  checked={selected}
                  onChange={() => setTheme(t.value)}
                  className="h-4 w-4 accent-[var(--primary-blue)]"
                />
                <i aria-hidden="true" className={t.icon} />
                {t.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    </Card>
  );
}

function ChangePasswordCard() {
  const { username, replaceSession, logout } = useAuth();
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setDone(false);
    const parsed = changePasswordRequestSchema.safeParse({ currentPassword: current, newPassword: next });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    if (next !== confirm) {
      setError("The new passwords don't match");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      replaceSession(await changePassword(parsed.data));
      void queryClient.invalidateQueries({ queryKey: ['settings-history'] });
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        logout();
        return;
      }
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const type = show ? 'text' : 'password';
  return (
    <Card
      title="Change password"
      description="Changes the dashboard sign-in password. Every other signed-in session is signed out; this one stays signed in."
    >
      <form onSubmit={onSubmit} className="max-w-md space-y-4" noValidate>
        {/* Lets password managers associate the new password with the right account. */}
        <input type="text" name="username" autoComplete="username" value={username ?? ''} readOnly hidden />
        <Field label="Current password" htmlFor="pw-current">
          <TextInput
            id="pw-current"
            type={type}
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </Field>
        <Field label="New password" hint="At least 12 characters." htmlFor="pw-new">
          <TextInput id="pw-new" type={type} autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Confirm new password" htmlFor="pw-confirm">
          <TextInput
            id="pw-confirm"
            type={type}
            autoComplete="new-password"
            value={confirm}
            invalid={confirm.length > 0 && confirm !== next}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </Field>
        <label className="flex min-h-[44px] cursor-pointer items-center gap-2.5 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => setShow(e.target.checked)}
            className="h-4 w-4 accent-[var(--primary-blue)]"
          />
          Show passwords
        </label>

        <div aria-live="polite">
          {error && (
            <p className="text-sm font-medium text-error">
              <i aria-hidden="true" className="fa-solid fa-circle-exclamation mr-1.5" />
              {error}
            </p>
          )}
          {done && (
            <p className="text-sm text-text-muted">
              <i aria-hidden="true" className="fa-solid fa-check mr-1.5 text-success" />
              Password changed.
            </p>
          )}
        </div>

        <Button type="submit" disabled={saving || !current || !next || !confirm}>
          {saving ? 'Changing…' : 'Change password'}
        </Button>
      </form>
      <p className="mt-5 border-t border-border pt-4 text-xs text-text-muted">
        Forgot the password? An operator can restart the API with <span className="font-mono">DASHBOARD_PASSWORD_RESET=true</span>{' '}
        to restore the configured <span className="font-mono">DASHBOARD_PASSWORD</span>.
      </p>
    </Card>
  );
}
